import { Alert, Box, Button, Divider, FormControlLabel, Grid, Icon, IconButton, Modal, Switch, Typography, useMediaQuery, useTheme } from "@mui/material";
import { FormHandles } from "@unform/core";
import React, { useEffect, useRef, useState } from "react";
import * as yup from "yup";
import { ToastContent } from "../../../shared/components";
import { LoadingComp } from "../../../shared/components/loading/Loading";
import { VForm, VTextField } from "../../../shared/forms";
import { useToast } from "../../../shared/hooks";
import { BannerService } from "../../../shared/services/api/banners/BannerService";
import { resizeImage, formatImageSrc } from "../../../shared/utils/image-utils";
import { BannerFileUploadArea } from "./BannerFileUploadArea/BannerFileUploadArea";

interface BannerModalProps {
    open: boolean;
    onClose: () => void;
    onSave: () => void;
    id?: string | null;
    initialImage?: string;
}

export const BannerModal: React.FC<BannerModalProps> = ({ open, onClose, onSave, id, initialImage }) => {
    const theme = useTheme();
    const smDown = useMediaQuery(theme.breakpoints.down("sm"));
    const formRef = useRef<FormHandles>(null);
    const toastHandler = useToast();

    const [isLoading, setIsLoading] = useState(false);
    const [active, setActive] = useState(true);
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);

    useEffect(() => {
        if (open) {
            if (id) {
                // Reset file state when opening for edit
                setImageFile(null);

                // Set initial image immediately if available
                if (initialImage) {
                    setPreviewUrl(formatImageSrc(initialImage) || null);
                }

                setIsLoading(true);
                BannerService.getById(id).then((result) => {
                    setIsLoading(false);
                    if (result instanceof Error) {
                        toastHandler.present({
                            type: "error",
                            messageOrContent: result.message,
                        });
                        onClose();
                    } else {
                        formRef.current?.setData({
                            title: result.title,
                            link: result.link,
                        });
                        setActive(result.active);
                        // Use fetched image, or fallback to initialImage if fetched is empty
                        const img = result.image || result.imageUrl || initialImage;
                        setPreviewUrl(formatImageSrc(img) || null);
                    }
                });
            } else {
                formRef.current?.setData({
                    title: "",
                    link: "",
                });
                setActive(true);
                setImageFile(null);
                setPreviewUrl(null);
            }
        } else {
            // Cleanup when closing
            if (previewUrl && !previewUrl.startsWith('data:') && !previewUrl.startsWith('http')) {
                URL.revokeObjectURL(previewUrl);
            }
            setPreviewUrl(null);
            setImageFile(null);
        }
    }, [open, id, initialImage]);

    const handleFileSelect = (file: File) => {
        if (previewUrl && !previewUrl.startsWith('data:') && !previewUrl.startsWith('http')) {
            URL.revokeObjectURL(previewUrl);
        }
        setImageFile(file);
        setPreviewUrl(URL.createObjectURL(file));
    };



    const handleSubmit = async (data: any) => {
        try {
            const schema = yup.object().shape({
                title: yup.string().required("Título é obrigatório"),
                link: yup.string().url("Insira um link válido (ex: https://...)").nullable(),
            });

            await schema.validate(data, { abortEarly: false });

            if (!id && !imageFile) {
                toastHandler.present({
                    type: "warning",
                    messageOrContent: "Por favor, selecione uma imagem para o novo banner.",
                });
                return;
            }

            setIsLoading(true);

            let base64Image = "";
            if (imageFile) {
                // Resize and compress image before sending
                base64Image = await resizeImage(imageFile);
            }

            const payload: any = {
                title: data.title,
                active: active,
                image: base64Image, // Send Base64 string
            };

            if (data.link) {
                payload.link = data.link;
            }

            let result;
            if (id) {
                // For update, only send image if it changed (base64Image is not empty)
                // If no new image, send undefined or empty string depending on backend requirement.
                // Here we send what we have. If base64Image is empty, backend should ignore or keep old.
                // But typically we merge with existing data or send only changed fields.
                // Let's assume we send the payload.
                result = await BannerService.updateById(id, payload);
            } else {
                result = await BannerService.create(payload as any); // Type assertion for Create
            }

            setIsLoading(false);

            if (result instanceof Error) {
                toastHandler.present({
                    type: "error",
                    messageOrContent: result.message,
                });
            } else {
                toastHandler.present({
                    type: "success",
                    messageOrContent: <ToastContent title="Sucesso" content="Banner salvo com sucesso!" />,
                });
                onSave();
                onClose();
            }
        } catch (err: any) {
            if (err instanceof yup.ValidationError) {
                const validationErrors: { [key: string]: string } = {};
                err.inner.forEach((error) => {
                    if (error.path) validationErrors[error.path] = error.message;
                });
                formRef.current?.setErrors(validationErrors);
            } else {
                toastHandler.present({
                    type: "error",
                    messageOrContent: "Ocorreu um erro inesperado ao salvar o banner.",
                });
            }
            setIsLoading(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Box
                sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    bgcolor: "background.paper",
                    boxShadow: 24,
                    p: 4,
                    width: "95%",
                    maxWidth: 600,
                    maxHeight: "90vh",
                    overflowY: "auto",
                    borderRadius: 3,
                }}
            >
                <LoadingComp isopen={isLoading} />

                <Grid container justifyContent="space-between" alignItems="center">
                    <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="h5" sx={{ color: "primary.main", fontWeight: 300 }}>
                            {id ? "Editar Banner" : "Novo Banner"}
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} size="small">
                        <Icon>close</Icon>
                    </IconButton>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <VForm ref={formRef} onSubmit={handleSubmit} onPointerEnterCapture={''} onPointerLeaveCapture={''} placeholder="">
                    <Box display="flex" flexDirection="column" gap={2}>
                        <VTextField
                            name="title"
                            label="Título"
                            fullWidth
                        />
                        <VTextField
                            name="link"
                            label="Link (Opcional)"
                            fullWidth
                        />

                        <FormControlLabel
                            control={<Switch checked={active} onChange={(e) => setActive(e.target.checked)} />}
                            label="Ativo"
                        />

                        <Box>
                            <Typography variant="subtitle2" gutterBottom>
                                Imagem do Banner
                            </Typography>
                            <Alert severity="warning">
                                <strong>Atenção:</strong> Para evitar cortes ou espaços em branco, utilize imagens com dimensões exatas de <strong>1200x220 pixels</strong>.
                            </Alert>

                            <BannerFileUploadArea onFileSelect={handleFileSelect} />
                        </Box>

                        {previewUrl && (
                            <Box mt={2} display="flex" flexDirection="column" alignItems="center">
                                <Typography variant="caption" color="textSecondary" gutterBottom>
                                    Pré-visualização:
                                </Typography>
                                <img src={previewUrl} alt="Preview" style={{ maxWidth: "100%", maxHeight: "200px", borderRadius: "8px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }} />
                            </Box>
                        )}

                        <Grid container justifyContent="flex-end" gap={2} mt={3}>
                            <Button
                                color="inherit"
                                variant="text"
                                onClick={onClose}
                                startIcon={<Icon>close</Icon>}
                            >
                                Cancelar
                            </Button>
                            <Button
                                color="primary"
                                variant="contained"
                                type="submit"
                                startIcon={<Icon>save</Icon>}
                            >
                                Salvar
                            </Button>
                        </Grid>
                    </Box>
                </VForm>
            </Box>
        </Modal>
    );
};
